import { lazy, ComponentType } from "react";

/**
 * Wraps React.lazy with auto-recovery from "Importing a module script failed"
 * errors caused by stale cached index.html referencing chunk hashes that no
 * longer exist after a redeploy. On first failure we force a one-time hard
 * reload; subsequent failures (real network/code errors) are re-thrown.
 */
const RELOAD_KEY = "lazy-chunk-reloaded";

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Successful load → clear the reload marker for future failures.
      try { window.sessionStorage.removeItem(RELOAD_KEY); } catch {}
      return mod;
    } catch (err: any) {
      const message = String(err?.message || err || "");
      const isChunkError =
        message.includes("Importing a module script failed") ||
        message.includes("Failed to fetch dynamically imported module") ||
        message.includes("error loading dynamically imported module") ||
        message.includes("Load failed");
      let alreadyReloaded = false;
      try { alreadyReloaded = window.sessionStorage.getItem(RELOAD_KEY) === "1"; } catch {}
      if (isChunkError && !alreadyReloaded) {
        try { window.sessionStorage.setItem(RELOAD_KEY, "1"); } catch {}
        window.location.reload();
        // Return a never-resolving promise so Suspense stays in fallback
        // until the reload happens.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
